// src/pages/admin/AdminListings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllListings, deleteListing } from '../../services/ListingsService';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './Admin.css';

function AdminListings() {
  const { currentUser } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedListingForMembers, setSelectedListingForMembers] = useState(null);
  const [showMembersList, setShowMembersList] = useState(false);
  const [membersData, setMembersData] = useState([]);
  
  // Fetch all listings
  useEffect(() => {
    const fetchListings = async () => {
      try {
        const data = await getAllListings();
        setListings(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching listings:", err);
        setError("Failed to load listings. Please try again later.");
        setLoading(false);
      }
    };

    fetchListings();
  }, []);

  // Filter listings based on search and category
  const filteredListings = listings.filter(listing => {
    const matchesSearch = searchTerm === '' || 
      listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === '' || listing.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle listing deletion
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteListing(id);
      setListings(listings.filter(listing => listing.id !== id));
      setSuccess("Listing deleted successfully");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error deleting listing:", err);
      setError("Failed to delete listing. Please try again.");
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };

  // Get members for a specific listing (admin version)
  const getMembersForListing = async (listingId) => {
    try {
      // Query payments for this specific listing
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('listingId', '==', listingId),
        where('status', '==', 'succeeded')
      );
      
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const payments = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));

      // Get user details for each payment
      const membersWithDetails = [];
      for (const payment of payments) {
        try {
          const userRef = doc(db, 'users', payment.userId);
          const userDoc = await getDoc(userRef);
          
          const userData = userDoc.exists() ? userDoc.data() : {
            displayName: 'Unknown User',
            email: 'No email available'
          };

          membersWithDetails.push({
            id: payment.id,
            name: userData.displayName || 'Unknown User',
            email: userData.email || 'No email available',
            purchaseDate: payment.createdAt || new Date(),
            amount: payment.amount,
            quantity: payment.quantity || 1,
            paymentId: payment.id
          });
        } catch (err) {
          console.error(`Error fetching user data for payment ${payment.id}:`, err);
        }
      }

      return membersWithDetails.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
    } catch (err) {
      console.error("Error fetching members for listing:", err);
      return [];
    }
  };

  // Show members list for a listing
  const showMembersForListing = async (listingId, listingTitle) => {
    setSelectedListingForMembers({ id: listingId, title: listingTitle });
    setShowMembersList(true);
    
    // Fetch members data
    const members = await getMembersForListing(listingId);
    setMembersData(members);
  };

  // Close members list
  const closeMembersList = () => {
    setShowMembersList(false);
    setSelectedListingForMembers(null);
    setMembersData([]);
  };

  // Format time for display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="admin-container">Loading listings data...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Listings Management</h1>
        <p>View and manage all listings on the platform</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="admin-menu">
        <Link to="/admin" className="admin-menu-item">
          Applications
        </Link>
        <Link to="/admin/users" className="admin-menu-item">
          Users
        </Link>
        <Link to="/admin/listings" className="admin-menu-item active">
          Listings
        </Link>
      </div>
      
      <div className="admin-content">
        <div className="section-header">
          <div className="section-header-content">
            <div>
              <h2>All Listings</h2>
              <p>{filteredListings.length} listing(s) found</p>
            </div>
            <Link to="/admin/add-listing" className="add-listing-button">
              Add New Listing
            </Link>
          </div>
        </div>
        
        <div className="admin-filters">
          <input
            type="text"
            placeholder="Search listings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
          
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="admin-filter-select"
          >
            <option value="">All Categories</option>
            <option value="education">Education</option>
            <option value="recreation">Recreation</option>
            <option value="childcare">Childcare</option>
            <option value="mentorship">Mentorship</option>
            <option value="professional_development">Professional Development</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        {filteredListings.length === 0 ? (
          <div className="empty-state">
            <p>No listings found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="listings-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Posted On</th>
                  <th>Location</th>
                  <th>Price</th>
                  <th>Members</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredListings.map(listing => (
                  <tr key={listing.id}>
                    <td>
                      <Link to={`/listings/${listing.id}`} className="listing-link">
                        {listing.title}
                      </Link>
                      {listing.status === 'pending' && (
                        <span className="status-badge" style={{ marginLeft: '0.5rem', backgroundColor: '#fff3cd', color: '#856404' }}>
                          Pending approval
                        </span>
                      )}
                      {listing.status === 'rejected' && (
                        <span className="status-badge" style={{ marginLeft: '0.5rem', backgroundColor: '#f8d7da', color: '#721c24' }}>
                          Rejected
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`category-badge ${listing.category}`}>
                        {listing.category.charAt(0).toUpperCase() + listing.category.slice(1).replace('_', ' ')}
                      </span>
                    </td>
                    <td>{formatDate(listing.createdAt)}</td>
                    <td>{listing.location}</td>
                    <td>${parseFloat(listing.price).toFixed(2)}</td>
                    <td>
                      <button
                        className="view-members-btn admin-members-btn"
                        onClick={() => showMembersForListing(listing.id, listing.title)}
                        title="View all members who signed up for this listing"
                      >
                        View Members
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link to={`/listings/${listing.id}`} className="view-action">
                          View
                        </Link>
                        <Link to={`/edit-listing/${listing.id}`} className="edit-action">
                          Edit
                        </Link>
                        <button 
                          onClick={() => handleDelete(listing.id)} 
                          className="delete-action"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Members List Modal */}
        {showMembersList && selectedListingForMembers && (
          <div className="members-modal-overlay">
            <div className="members-modal">
              <div className="members-modal-header">
                <h3>Members for "{selectedListingForMembers.title}"</h3>
                <button className="close-modal-btn" onClick={closeMembersList}>×</button>
              </div>
              
              <div className="members-modal-content">
                {membersData.length === 0 ? (
                  <div className="no-members-message">
                    <p>No members have signed up for this listing yet.</p>
                  </div>
                ) : (
                  <div className="members-table-container">
                    <table className="members-table">
                    <thead>
                      <tr>
                        <th>Member Name</th>
                        <th>Email</th>
                        <th>Purchase Date</th>
                        <th>Qty</th>
                        <th>Amount Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {membersData.map((member) => (
                        <tr key={member.id}>
                          <td className="member-name">{member.name}</td>
                          <td className="member-email">{member.email}</td>
                          <td className="member-date">
                            <div>{formatDate(member.purchaseDate)}</div>
                            <div className="member-time">{formatTime(member.purchaseDate)}</div>
                          </td>
                          <td className="member-qty">{member.quantity || 1}</td>
                          <td className="member-amount">${member.amount?.toFixed(2) || '0.00'}</td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="members-modal-footer">
                <p><strong>Total Members:</strong> {membersData.length}</p>
                <button className="close-modal-btn-footer" onClick={closeMembersList}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminListings;
